import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const normalizeMatrixLine = (line = {}) => ({
  sourceMatrixItemId: line._id || line.sourceMatrixItemId || null,
  category: line.category || '',
  competence: line.competence || line.easyRedmineSkill || '',
  detail: line.detail || '',
  level: line.level || line.profile || '',
  profile: line.profile || 'admin',
  easyRedmineSkill: line.easyRedmineSkill || line.competence || '',
  easyRedmineLevel: line.easyRedmineLevel || '',
  matrixLevel: line.matrixLevel || '',
  active: line.active !== false,
  included: line.included !== false,
});

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const buildMatrixMatchKey = (line = {}) => [
  normalizeText(line.category),
  normalizeText(line.easyRedmineSkill || line.competence),
  normalizeText(line.easyRedmineLevel),
  normalizeText(line.profile || line.level),
].join('||');

const buildContractMatrixFromBase = (matrix, currentCompetences = []) => {
  const normalizedCurrentCompetences = (currentCompetences || []).map(normalizeMatrixLine);
  const currentBySourceId = new Map(
    normalizedCurrentCompetences
      .filter((item) => item?.sourceMatrixItemId)
      .map((item) => [String(item.sourceMatrixItemId), item])
  );
  const currentByCompositeKey = new Map(
    normalizedCurrentCompetences.map((item) => [buildMatrixMatchKey(item), item])
  );

  return (matrix?.competences || []).map((line) => {
    const normalized = normalizeMatrixLine(line);
    const existingBySourceId = normalized.sourceMatrixItemId
      ? currentBySourceId.get(String(normalized.sourceMatrixItemId))
      : null;
    const existingByCompositeKey = currentByCompositeKey.get(buildMatrixMatchKey(normalized));
    const existing = existingBySourceId || existingByCompositeKey || null;

    if (!existing) {
      return normalized;
    }

    return {
      ...normalized,
      sourceMatrixItemId: normalized.sourceMatrixItemId || existing.sourceMatrixItemId || null,
      included: existing.included !== false,
      active: existing.active !== false,
    };
  });
};

const mergeContractMatrixWithBase = (matrix, currentCompetences = []) => {
  const mergedFromBase = buildContractMatrixFromBase(matrix, currentCompetences);
  const mergedSourceIds = new Set(
    mergedFromBase
      .map((item) => item?.sourceMatrixItemId)
      .filter(Boolean)
      .map((value) => String(value))
  );

  const orphanLines = (currentCompetences || []).filter((item) => {
    const normalizedItem = normalizeMatrixLine(item);
    if (mergedSourceIds.has(String(normalizedItem.sourceMatrixItemId))) {
      return false;
    }
    const existsInBase = mergedFromBase.some(
      (mergedItem) => buildMatrixMatchKey(mergedItem) === buildMatrixMatchKey(normalizedItem)
    );
    if (existsInBase) {
      return false;
    }
    if (!item?.sourceMatrixItemId) {
      return true;
    }
    return !mergedSourceIds.has(String(item.sourceMatrixItemId));
  });

  return [...mergedFromBase, ...orphanLines.map(normalizeMatrixLine)];
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const SERVICE_METRIC_KEYS = ['admin_hours', 'engineer_hours', 'admin_days', 'engineer_days', 'pilotage_days', 'travel'];
const DEFAULT_SECTION = 'GENERAL';

const serviceMetricFieldMap = {
  admin_hours: { quantityField: 'adminHours', unitPriceField: 'adminHourlyRate', billingCycle: 'mensuel' },
  engineer_hours: { quantityField: 'engineerHours', unitPriceField: 'engineerHourlyRate', billingCycle: 'mensuel' },
  admin_days: { quantityField: 'adminDays', unitPriceField: 'adminDailyRate', billingCycle: 'annuel' },
  engineer_days: { quantityField: 'engineerDays', unitPriceField: 'engineerDailyRate', billingCycle: 'annuel' },
  pilotage_days: { quantityField: 'pilotageDays', unitPriceField: 'pilotageDailyRate', billingCycle: 'annuel' },
  travel: { quantityField: 'travelCount', unitPriceField: 'travelUnitPrice', billingCycle: 'annuel' },
};

const normalizeServiceMetric = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return SERVICE_METRIC_KEYS.includes(normalized) ? normalized : '';
};

const normalizeProductCode = (value = '') =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const normalizeSectionLabel = (value = '') => {
  const cleaned = String(value || '')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned ? cleaned.toUpperCase() : DEFAULT_SECTION;
};

const normalizeCategoryText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const isInfogeranceServiceCategory = (category = '', subCategory = '') => {
  const normalizedCategory = normalizeCategoryText(category);
  const normalizedSubCategory = normalizeCategoryText(subCategory);
  return (normalizedCategory === 'service' || normalizedCategory === 'services')
    && (normalizedSubCategory === 'infogerance' || normalizedSubCategory === 'infogerances');
};

const detectServiceMetricFromText = (value = '') => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (normalized.includes('heure') && normalized.includes('admin')) return 'admin_hours';
  if (normalized.includes('heure') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_hours';
  if (normalized.includes('jour') && normalized.includes('admin')) return 'admin_days';
  if (normalized.includes('jour') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_days';
  if (normalized.includes('pilotage')) return 'pilotage_days';
  if (normalized.includes('deplacement')) return 'travel';
  return '';
};

const detectServiceMetricFromCode = (code = '') => {
  const normalized = normalizeProductCode(code);
  if (!normalized) return '';
  if (normalized.includes('TRAVEL') || normalized.includes('DEPLACEMENT')) return 'travel';
  if (normalized.includes('PILOTAGE') && normalized.includes('DAY')) return 'pilotage_days';
  if (normalized.includes('ADMIN') && normalized.includes('HOUR')) return 'admin_hours';
  if (normalized.includes('ENGINEER') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('INGE') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('ADMIN') && normalized.includes('DAY')) return 'admin_days';
  if (normalized.includes('ENGINEER') && normalized.includes('DAY')) return 'engineer_days';
  if (normalized.includes('INGE') && normalized.includes('DAY')) return 'engineer_days';
  return '';
};

const resolveServiceMetric = ({ serviceMetric, productCode, productName } = {}) => (
  normalizeServiceMetric(serviceMetric)
  || detectServiceMetricFromCode(productCode)
  || detectServiceMetricFromText(productName)
);

const getArticleLineOrder = (line = {}) => {
  const parsed = Number(line?.lineOrder);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildLegacyServiceFieldsFromArticles = (articles = []) => {
  const computed = {
    adminHours: 0,
    engineerHours: 0,
    pilotageHours: 0,
    adminHourlyRate: 0,
    engineerHourlyRate: 0,
    pilotageHourlyRate: 0,
    adminDays: 0,
    engineerDays: 0,
    pilotageDays: 0,
    adminDailyRate: 0,
    engineerDailyRate: 0,
    pilotageDailyRate: 0,
    travelUnitPrice: 0,
    travelCount: 0,
  };

  (articles || []).forEach((line) => {
    const metric = resolveServiceMetric({
      serviceMetric: line?.serviceMetric,
      productCode: line?.productCode,
      productName: line?.productName,
    });
    if (!metric || !serviceMetricFieldMap[metric]) return;
    const { quantityField, unitPriceField } = serviceMetricFieldMap[metric];
    const quantity = toNumber(line?.quantity);
    const unitPrice = toNumber(line?.unitPrice);
    computed[quantityField] += quantity;
    if (unitPrice > 0) computed[unitPriceField] = unitPrice;
  });

  computed.pilotageHours = computed.pilotageDays * 7;
  computed.pilotageHourlyRate = computed.pilotageDailyRate > 0 ? computed.pilotageDailyRate / 7 : 0;
  return computed;
};

const isServiceArticleLine = (line = {}) => {
  const metric = resolveServiceMetric({
    serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
    productCode: line?.productCode || line?.article?.code,
    productName: line?.productName || line?.article?.name,
  });
  if (metric) return true;
  return isInfogeranceServiceCategory(
    line?.category || line?.article?.category || '',
    line?.subCategory || line?.article?.subCategory || ''
  );
};

const SECTION_SORT_ORDER = [
  'INFOGERANCE',
  'SERVICES - INFOGERANCE',
  'VM - CODIAL',
  'HOSTING - SAUVEGARDE',
  'HOSTING - PRESTATION HEBERGEMENT',
  'GENERAL',
];

const normalizeSectionSortKey = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const getSectionSortRank = (section = '') => {
  const key = normalizeSectionSortKey(section || DEFAULT_SECTION);
  const index = SECTION_SORT_ORDER.indexOf(key);
  return index >= 0 ? index : 999;
};

const getBillingCycleSortRank = (cycle = '') => {
  const normalized = String(cycle || '').trim().toLowerCase();
  if (normalized === 'mensuel') return 0;
  if (normalized === 'annuel') return 1;
  return 2;
};

const getServiceArticleSortRank = (line = {}) => {
  const metric = resolveServiceMetric({
    serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
    productCode: line?.productCode || line?.article?.code,
    productName: line?.productName || line?.article?.name,
  });
  if (metric === 'admin_hours') return 0;
  if (metric === 'engineer_hours') return 1;
  if (metric === 'admin_days') return 2;
  if (metric === 'engineer_days') return 3;
  if (metric === 'pilotage_days') return 4;
  if (metric === 'travel') return 7;

  const normalizedName = normalizeCategoryText(line?.productName || line?.article?.name || '');
  if (normalizedName.includes('helpdesk')) return 5;
  if (normalizedName.includes('frais') && normalizedName.includes('prelev')) return 6;
  if (normalizedName.includes('deplacement')) return 7;
  return 50;
};

const compareContractArticleLines = (left = {}, right = {}) => {
  const leftLineOrder = getArticleLineOrder(left);
  const rightLineOrder = getArticleLineOrder(right);
  if (leftLineOrder != null && rightLineOrder != null && leftLineOrder !== rightLineOrder) {
    return leftLineOrder - rightLineOrder;
  }
  if (leftLineOrder != null && rightLineOrder == null) return -1;
  if (leftLineOrder == null && rightLineOrder != null) return 1;

  const sectionRankDiff = getSectionSortRank(left.section) - getSectionSortRank(right.section);
  if (sectionRankDiff !== 0) return sectionRankDiff;

  const sectionNameDiff = normalizeSectionLabel(left.section || DEFAULT_SECTION).localeCompare(
    normalizeSectionLabel(right.section || DEFAULT_SECTION),
    'fr',
  );
  if (sectionNameDiff !== 0) return sectionNameDiff;

  const typeRankLeft = isServiceArticleLine(left) ? 0 : 1;
  const typeRankRight = isServiceArticleLine(right) ? 0 : 1;
  if (typeRankLeft !== typeRankRight) return typeRankLeft - typeRankRight;

  if (typeRankLeft === 0 && typeRankRight === 0) {
    const serviceRankDiff = getServiceArticleSortRank(left) - getServiceArticleSortRank(right);
    if (serviceRankDiff !== 0) return serviceRankDiff;
  }

  const billingRankDiff = getBillingCycleSortRank(left.billingCycle) - getBillingCycleSortRank(right.billingCycle);
  if (billingRankDiff !== 0) return billingRankDiff;

  const categoryDiff = String(left.category || '').localeCompare(String(right.category || ''), 'fr');
  if (categoryDiff !== 0) return categoryDiff;

  const subCategoryDiff = String(left.subCategory || '').localeCompare(String(right.subCategory || ''), 'fr');
  if (subCategoryDiff !== 0) return subCategoryDiff;

  const nameDiff = String(left.productName || '').localeCompare(String(right.productName || ''), 'fr');
  if (nameDiff !== 0) return nameDiff;

  return String(left.productCode || '').localeCompare(String(right.productCode || ''), 'fr');
};

const sortContractArticleLines = (lines = []) =>
  [...lines]
    .sort(compareContractArticleLines)
    .map((line, index) => ({ ...line, lineOrder: index + 1 }));

const formatEuro = (value) =>
  `${toNumber(value).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const resolveContractType = (contract = {}) => {
  const rawType = normalizeText(contract?.contractType);
  if (rawType === 'cadre' || rawType === 'infogerance' || rawType === 'basique' || rawType === 'hebergement') return rawType;
  if (contract?.cadreContract === true) return 'cadre';
  if (contract?.infogerance === true) return 'infogerance';
  return '';
};

const buildInitialForm = (editing) => ({
  name: editing?.name || '',
  reference: editing?.reference || '',
  service: editing?.service || '',
  Organization: editing?.Organization?._id || editing?.Organization || '',
  baseMatrixId: editing?.baseMatrixId?._id || editing?.baseMatrixId || '',
  support: Array.isArray(editing?.support) ? editing.support : [],
  contractType: resolveContractType(editing),
  infogerance: resolveContractType(editing) === 'infogerance',
  cadreContract: resolveContractType(editing) === 'cadre',
  startDate: toDateInputValue(editing?.startDate),
  endDate: toDateInputValue(editing?.endDate),
  adminHours: editing?.adminHours ?? 0,
  engineerHours: editing?.engineerHours ?? 0,
  pilotageHours: editing?.pilotageHours ?? 0,
  adminHourlyRate: editing?.adminHourlyRate ?? 0,
  engineerHourlyRate: editing?.engineerHourlyRate ?? 0,
  pilotageHourlyRate: editing?.pilotageHourlyRate ?? 0,
  adminDays: editing?.adminDays ?? 0,
  engineerDays: editing?.engineerDays ?? 0,
  pilotageDays: editing?.pilotageDays ?? (editing?.pilotageHours ? Number(editing.pilotageHours) / 7 : 0),
  adminDailyRate: editing?.adminDailyRate ?? 0,
  engineerDailyRate: editing?.engineerDailyRate ?? 0,
  pilotageDailyRate: editing?.pilotageDailyRate ?? (editing?.pilotageHourlyRate ? Number(editing.pilotageHourlyRate) * 7 : 0),
  projectConsultingAdminDays: 0,
  projectConsultingEngineerDays: 0,
  projectConsultingAdminDailyRate: 0,
  projectConsultingEngineerDailyRate: 0,
  travelUnitPrice: editing?.travelUnitPrice ?? 0,
  travelCount: editing?.travelCount ?? 0,
  billingFrequency: editing?.billingFrequency || 'mensuel',
  paymentType: editing?.paymentType || '',
  competences: Array.isArray(editing?.competences) ? editing.competences.map(normalizeMatrixLine) : [],
  articles: Array.isArray(editing?.articles)
    ? sortContractArticleLines(editing.articles.map((line) => ({
        article: line.article?._id || line.article || null,
        productCode: line.productCode || line.article?.code || '',
        productName: line.productName || line.article?.name || '',
        category: line.category || line.article?.category || '',
        subCategory: line.subCategory || line.article?.subCategory || '',
        section: normalizeSectionLabel(line.section || DEFAULT_SECTION),
        serviceMetric: resolveServiceMetric({
          serviceMetric: line.serviceMetric || line.article?.serviceMetric,
          productCode: line.productCode || line.article?.code,
          productName: line.productName || line.article?.name,
        }),
        lineOrder: line.lineOrder,
        billingCycle: line.billingCycle || line.article?.billingCycle || 'oneshot',
        quantity: line.quantity ?? 1,
        unitPrice: line.unitPrice ?? '',
        description: line.description || '',
      })))
    : [],
});

export default function ContratFormDialog({
  open,
  onClose,
  onSaved,
  editing,
  organizations,
  matrices,
  articles,
  supportProjects,
  api,
}) {
  const [form, setForm] = useState(buildInitialForm(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newProductCategory, setNewProductCategory] = useState('');
  const [newProductSubCategory, setNewProductSubCategory] = useState('');
  const [newProductId, setNewProductId] = useState('');
  const [newProductSection, setNewProductSection] = useState(DEFAULT_SECTION);
  const [draggingArticleIndex, setDraggingArticleIndex] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const organizationOptions = useMemo(
    () => organizations.map((org) => ({ label: org.name || 'Sans nom', value: org._id })),
    [organizations]
  );

  const matrixOptions = useMemo(
    () => matrices.map((matrix) => ({ label: matrix.name || 'Sans nom', value: matrix._id })),
    [matrices]
  );
  const selectedOrganizationName = useMemo(() => {
    const byId = organizations.find((org) => String(org._id) === String(form.Organization));
    if (byId?.name) return byId.name;
    return editing?.Organization?.name || '';
  }, [editing?.Organization?.name, form.Organization, organizations]);

  const selectedMatrix = useMemo(
    () => matrices.find((matrix) => String(matrix._id) === String(form.baseMatrixId)) || null,
    [form.baseMatrixId, matrices]
  );

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(editing));
      setError('');
      setNewProductCategory('');
      setNewProductSubCategory('');
      setNewProductId('');
      setNewProductSection(DEFAULT_SECTION);
      setDraggingArticleIndex(null);
      setDragOverTarget(null);
    }
  }, [editing, open]);

  useEffect(() => {
    if (!open) return;
    if (!selectedMatrix) return;
    const mergedCompetences = mergeContractMatrixWithBase(selectedMatrix, form.competences);
    const currentSignature = JSON.stringify((form.competences || []).map(normalizeMatrixLine));
    const mergedSignature = JSON.stringify(mergedCompetences.map(normalizeMatrixLine));

    if (currentSignature === mergedSignature) return;

    setForm((current) => ({
      ...current,
      competences: mergeContractMatrixWithBase(selectedMatrix, current.competences),
    }));
  }, [form.competences, open, selectedMatrix]);


  const articleOptions = useMemo(
    () =>
      articles.map((article) => ({
        label: `${article.code ? `${article.code} - ` : ''}${article.name}${article.category ? ` (${article.category}${article.subCategory ? ` / ${article.subCategory}` : ''})` : ''}`,
        value: article._id,
        code: article.code || '',
        name: article.name,
        category: article.category || '',
        subCategory: article.subCategory || '',
        serviceMetric: resolveServiceMetric({
          serviceMetric: article.serviceMetric,
          productCode: article.code,
          productName: article.name,
        }),
        billingCycle: article.billingCycle || 'oneshot',
        unitPrice: article.unitPrice,
        defaultQuantity: article.defaultQuantity,
      })),
    [articles]
  );

  const supportOptions = useMemo(
    () =>
      (supportProjects || []).map((project) => ({
        label: project.projectName,
        value: {
          projectId: project.projectId,
          projectName: project.projectName,
        },
      })),
    [supportProjects]
  );

  const servicesArticles = useMemo(
    () => (form.articles || []).filter((line) => isServiceArticleLine(line)),
    [form.articles]
  );

  const productArticles = useMemo(
    () => (form.articles || []).filter((line) => !isServiceArticleLine(line)),
    [form.articles]
  );

  const serviceSectionOptions = useMemo(() => {
    const set = new Set([DEFAULT_SECTION, 'VM - CODIAL', 'HOSTING - SAUVEGARDE', 'HOSTING - PRESTATION HEBERGEMENT']);
    (form.articles || []).forEach((line) => {
      if (line.section) set.add(normalizeSectionLabel(line.section));
    });
    return Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [form.articles]);

  const articlesTotal = useMemo(
    () =>
      (form.articles || []).reduce(
        (sum, line) => sum + toNumber(line.quantity) * toNumber(line.unitPrice),
        0
      ),
    [form.articles]
  );

  const groupedArticleRows = useMemo(() => {
    const sortedEntries = (form.articles || [])
      .map((line, index) => ({ line, index }))
      .sort((a, b) => compareContractArticleLines(a.line, b.line));
    const grouped = new Map();
    sortedEntries.forEach(({ line, index }) => {
      const section = normalizeSectionLabel(line.section || DEFAULT_SECTION);
      const current = grouped.get(section) || [];
      current.push({ line, index });
      grouped.set(section, current);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
      .map(([section, rows]) => {
        const subtotalMonthly = rows.reduce((sum, item) => {
          const lineTotal = toNumber(item.line.quantity) * toNumber(item.line.unitPrice);
          if (item.line.billingCycle === 'mensuel') return sum + lineTotal;
          if (item.line.billingCycle === 'annuel') return sum + lineTotal / 12;
          return sum;
        }, 0);
        const subtotalAnnual = rows.reduce((sum, item) => {
          const lineTotal = toNumber(item.line.quantity) * toNumber(item.line.unitPrice);
          if (item.line.billingCycle === 'mensuel') return sum + lineTotal * 12;
          if (item.line.billingCycle === 'annuel') return sum + lineTotal;
          return sum + lineTotal;
        }, 0);
        const isMonthlyBilling = form.billingFrequency === 'mensuel';
        return {
          section,
          rows,
          subtotalMonthly,
          subtotalAnnual,
          subtotalDisplay: isMonthlyBilling ? subtotalMonthly : subtotalAnnual,
          subtotalUnitLabel: isMonthlyBilling ? '€/mois' : '€/an',
        };
      });
  }, [form.articles, form.billingFrequency]);

  const linesMonthlyValue = useMemo(
    () =>
      (form.articles || []).reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal;
        if (line.billingCycle === 'annuel') return sum + lineTotal / 12;
        return sum;
      }, 0),
    [form.articles]
  );

  const linesAnnualValue = useMemo(
    () =>
      (form.articles || []).reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal * 12;
        if (line.billingCycle === 'annuel') return sum + lineTotal;
        return sum + lineTotal;
      }, 0),
    [form.articles]
  );

  const serviceMonthlyValue = useMemo(
    () =>
      servicesArticles.reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal;
        if (line.billingCycle === 'annuel') return sum + lineTotal / 12;
        return sum;
      }, 0),
    [servicesArticles]
  );

  const serviceAnnualValue = useMemo(
    () =>
      servicesArticles.reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal * 12;
        if (line.billingCycle === 'annuel') return sum + lineTotal;
        return sum + lineTotal;
      }, 0),
    [servicesArticles]
  );

  const productsMonthlyValue = useMemo(
    () =>
      productArticles.reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal;
        if (line.billingCycle === 'annuel') return sum + lineTotal / 12;
        return sum;
      }, 0),
    [productArticles]
  );

  const productsAnnualValue = useMemo(
    () =>
      productArticles.reduce((sum, line) => {
        const lineTotal = toNumber(line.quantity) * toNumber(line.unitPrice);
        if (line.billingCycle === 'mensuel') return sum + lineTotal * 12;
        if (line.billingCycle === 'annuel') return sum + lineTotal;
        return sum + lineTotal;
      }, 0),
    [productArticles]
  );

  const globalMonthlyValue = useMemo(
    () => serviceMonthlyValue + productsMonthlyValue,
    [productsMonthlyValue, serviceMonthlyValue]
  );

  const globalAnnualValue = useMemo(
    () => serviceAnnualValue + productsAnnualValue,
    [productsAnnualValue, serviceAnnualValue]
  );

  const articleCategoryOptions = useMemo(() => {
    const set = new Set();
    articleOptions.forEach((option) => {
      if (option.category) set.add(option.category);
    });
    return Array.from(set).sort();
  }, [articleOptions]);

  const articleSubCategoryOptions = useMemo(() => {
    const set = new Set();
    articleOptions
      .filter((option) => !newProductCategory || option.category === newProductCategory)
      .forEach((option) => {
        if (option.subCategory) set.add(option.subCategory);
      });
    return Array.from(set).sort();
  }, [articleOptions, newProductCategory]);

  const selectableProductOptions = useMemo(
    () =>
      articleOptions.filter((option) => {
        if (newProductCategory && option.category !== newProductCategory) return false;
        if (newProductSubCategory && option.subCategory !== newProductSubCategory) return false;
        return true;
      }),
    [articleOptions, newProductCategory, newProductSubCategory]
  );

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateArticleLine = (index, key, value) => {
    setForm((current) => {
      const nextArticles = [...current.articles];
      nextArticles[index] = { ...nextArticles[index], [key]: value };
      return { ...current, articles: nextArticles };
    });
  };

  const reorderArticlesByDisplayIndexes = (sourceIndex, targetIndex, placement = 'before') => {
    if (sourceIndex == null || targetIndex == null || sourceIndex === targetIndex) return;
    setForm((current) => {
      const orderedEntries = (current.articles || [])
        .map((line, index) => ({ line, index }))
        .sort((left, right) => compareContractArticleLines(left.line, right.line));
      const sourcePosition = orderedEntries.findIndex((entry) => entry.index === sourceIndex);
      const targetPosition = orderedEntries.findIndex((entry) => entry.index === targetIndex);
      if (sourcePosition < 0 || targetPosition < 0) return current;

      const [moved] = orderedEntries.splice(sourcePosition, 1);
      let insertionPosition = placement === 'after' ? targetPosition + 1 : targetPosition;
      if (sourcePosition < insertionPosition) {
        insertionPosition -= 1;
      }
      orderedEntries.splice(Math.max(0, insertionPosition), 0, moved);

      return {
        ...current,
        articles: orderedEntries.map((entry, index) => ({ ...entry.line, lineOrder: index + 1 })),
      };
    });
  };

  const startArticleDrag = (event, index) => {
    setDraggingArticleIndex(index);
    setDragOverTarget(null);
    const rowElement = event?.currentTarget?.closest('tr');
    if (!event?.dataTransfer || !rowElement) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
    const rowRect = rowElement.getBoundingClientRect();
    event.dataTransfer.setDragImage(
      rowElement,
      Math.min(24, Math.max(0, rowRect.width / 2)),
      20,
    );
  };

  const endArticleDrag = () => {
    setDraggingArticleIndex(null);
    setDragOverTarget(null);
  };

  const dropArticleOn = (targetIndex, placement = 'before') => {
    if (draggingArticleIndex == null) return;
    reorderArticlesByDisplayIndexes(draggingArticleIndex, targetIndex, placement);
    setDraggingArticleIndex(null);
    setDragOverTarget(null);
  };

  const addProductFromSelector = () => {
    if (!newProductId) return;
    const selected = articleOptions.find((option) => option.value === newProductId);
    if (!selected) return;
    const detectedServiceMetric = resolveServiceMetric({
      serviceMetric: selected.serviceMetric,
      productCode: selected.code,
      productName: selected.name,
    });

    setForm((current) => ({
      ...current,
      articles: sortContractArticleLines([
        ...current.articles,
        {
          article: selected.value,
          productCode: selected.code || '',
          productName: selected.name || '',
          category: selected.category || '',
          subCategory: selected.subCategory || '',
          section: normalizeSectionLabel(newProductSection || DEFAULT_SECTION),
          serviceMetric: detectedServiceMetric,
          billingCycle: serviceMetricFieldMap[detectedServiceMetric]?.billingCycle || selected.billingCycle || 'oneshot',
          quantity: selected.defaultQuantity ?? 1,
          unitPrice: selected.unitPrice ?? 0,
          description: '',
        },
      ]),
    }));
    setNewProductId('');
  };

  const removeArticleLine = (index) => {
    setForm((current) => ({
      ...current,
      articles: sortContractArticleLines(current.articles.filter((_, lineIndex) => lineIndex !== index)),
    }));
  };

  const applyInfogeranceServicePackage = () => {
    const serviceProducts = articleOptions.filter((option) =>
      isInfogeranceServiceCategory(option.category, option.subCategory)
    );

    if (serviceProducts.length === 0) return;

    setForm((current) => {
      const existingMetrics = new Set(
        (current.articles || [])
          .map((line) => resolveServiceMetric({
            serviceMetric: line.serviceMetric,
            productCode: line.productCode,
            productName: line.productName,
          }))
          .filter(Boolean)
      );
      const existingProductIds = new Set(
        (current.articles || []).map((line) => String(line.article || '')).filter(Boolean)
      );

      const linesToAdd = serviceProducts
        .map((product) => {
          if (existingProductIds.has(String(product.value))) return null;
          const metric = resolveServiceMetric({
            serviceMetric: product.serviceMetric,
            productCode: product.code,
            productName: product.name,
          });
          if (metric && existingMetrics.has(metric)) return null;
          return {
            article: product.value,
            productCode: product.code || '',
            productName: product.name || '',
            category: product.category || '',
            subCategory: product.subCategory || '',
            section: normalizeSectionLabel('SERVICES - INFOGERANCE'),
            serviceMetric: metric || '',
            billingCycle: serviceMetricFieldMap[metric]?.billingCycle || product.billingCycle || 'oneshot',
            quantity: product.defaultQuantity ?? 0,
            unitPrice: product.unitPrice ?? 0,
            description: '',
          };
        })
        .filter(Boolean);

      if (linesToAdd.length === 0) return current;
      return {
        ...current,
        articles: sortContractArticleLines([...current.articles, ...linesToAdd]),
      };
    });
  };

  const applyMatrixToContract = (matrixId) => {
    const matrix = matrices.find((item) => String(item._id) === String(matrixId));
    setForm((current) => ({
      ...current,
      baseMatrixId: matrixId,
      competences: matrix ? mergeContractMatrixWithBase(matrix, current.competences) : [],
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const normalizedArticles = sortContractArticleLines((form.articles || [])
        .filter((line) => line.article)
        .map((line) => {
          const serviceMetric = resolveServiceMetric({
            serviceMetric: line.serviceMetric,
            productCode: line.productCode,
            productName: line.productName,
          });
          return {
            article: line.article,
            productCode: line.productCode || '',
            productName: line.productName || '',
            category: line.category || '',
            subCategory: line.subCategory || '',
            section: normalizeSectionLabel(line.section || DEFAULT_SECTION),
            serviceMetric: serviceMetric || undefined,
            lineOrder: Number(line.lineOrder || 0) || undefined,
            billingCycle: line.billingCycle || (serviceMetricFieldMap[serviceMetric]?.billingCycle || 'oneshot'),
            quantity: Number(line.quantity || 0),
            unitPrice: line.unitPrice === '' ? undefined : Number(line.unitPrice),
            description: line.description || '',
          };
        }));
      const legacyServiceFields = buildLegacyServiceFieldsFromArticles(normalizedArticles);

      const payload = {
        ...form,
        contractType: form.contractType || '',
        infogerance: form.contractType === 'infogerance',
        cadreContract: form.contractType === 'cadre',
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        adminHours: legacyServiceFields.adminHours,
        engineerHours: legacyServiceFields.engineerHours,
        pilotageHours: legacyServiceFields.pilotageHours,
        adminHourlyRate: legacyServiceFields.adminHourlyRate,
        engineerHourlyRate: legacyServiceFields.engineerHourlyRate,
        pilotageHourlyRate: legacyServiceFields.pilotageHourlyRate,
        adminDays: legacyServiceFields.adminDays,
        engineerDays: legacyServiceFields.engineerDays,
        pilotageDays: legacyServiceFields.pilotageDays,
        adminDailyRate: legacyServiceFields.adminDailyRate,
        engineerDailyRate: legacyServiceFields.engineerDailyRate,
        pilotageDailyRate: legacyServiceFields.pilotageDailyRate,
        projectConsultingAdminDays: 0,
        projectConsultingEngineerDays: 0,
        projectConsultingAdminDailyRate: 0,
        projectConsultingEngineerDailyRate: 0,
        travelUnitPrice: legacyServiceFields.travelUnitPrice,
        travelCount: legacyServiceFields.travelCount,
        paymentType: form.paymentType || '',
        support: Array.isArray(form.support) ? form.support : [],
        competences: (form.competences || []).map((item) => ({
          sourceMatrixItemId: item.sourceMatrixItemId || undefined,
          category: item.category || '',
          competence: item.competence || item.easyRedmineSkill || '',
          detail: item.detail || '',
          level: item.level || item.profile || '',
          profile: item.profile || 'admin',
          easyRedmineSkill: item.easyRedmineSkill || '',
          easyRedmineLevel: item.easyRedmineLevel || '',
          matrixLevel: item.matrixLevel || '',
          active: item.active !== false,
          included: item.included !== false,
        })),
        articles: normalizedArticles,
      };

      if (editing?._id) {
        await api.put(`/api/contrat/${editing._id}`, payload);
      } else {
        await api.post('/api/contrat', payload);
      }

      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        {editing?._id
          ? `Modifier le contrat${selectedOrganizationName ? ` - ${selectedOrganizationName}` : ''} - ${formatEuro(globalAnnualValue)}`
          : 'Nouveau contrat'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Informations generales</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nom du contrat"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Reference"
                      value={form.reference}
                      onChange={(e) => updateField('reference', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Service"
                      value={form.service}
                      onChange={(e) => updateField('service', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                      options={organizationOptions}
                      value={organizationOptions.find((option) => option.value === form.Organization) || null}
                      onChange={(_, value) => updateField('Organization', value?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Organisation" fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                      options={matrixOptions}
                      value={matrixOptions.find((option) => option.value === form.baseMatrixId) || null}
                      onChange={(_, value) => applyMatrixToContract(value?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Matrice" fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Date debut"
                      value={form.startDate}
                      onChange={(e) => updateField('startDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Date fin"
                      value={form.endDate}
                      onChange={(e) => updateField('endDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Facturation"
                      value={form.billingFrequency}
                      onChange={(e) => updateField('billingFrequency', e.target.value)}
                    >
                      <MenuItem value="mensuel">Mensuel</MenuItem>
                      <MenuItem value="annuel">Annuel</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Type de paiement"
                      value={form.paymentType || ''}
                      onChange={(e) => updateField('paymentType', e.target.value)}
                    >
                      <MenuItem value="">Non défini</MenuItem>
                      <MenuItem value="virement">Virement</MenuItem>
                      <MenuItem value="prelevement">Prélèvement</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Type de contrat"
                      value={form.contractType}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        updateField('contractType', nextType);
                        updateField('infogerance', nextType === 'infogerance');
                        updateField('cadreContract', nextType === 'cadre');
                        if (nextType === 'infogerance') {
                          applyInfogeranceServicePackage();
                        }
                      }}
                    >
                      <MenuItem value="">Non défini</MenuItem>
                      <MenuItem value="basique">Basique</MenuItem>
                      <MenuItem value="hebergement">Hébergement</MenuItem>
                      <MenuItem value="infogerance">Infogérance</MenuItem>
                      <MenuItem value="cadre">Cadre</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Perimetre support</Typography>
                <Autocomplete
                  multiple
                  options={supportOptions}
                  value={(form.support || []).map((item) => ({
                    label: item.projectName,
                    value: item,
                  }))}
                  isOptionEqualToValue={(option, value) => option.value.projectId === value.value.projectId}
                  onChange={(_, values) => updateField('support', values.map((item) => item.value))}
                  renderInput={(params) => (
                    <TextField {...params} label="Projets support EasyRedmine" fullWidth size="small" />
                  )}
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: '#f2fbf6',
                  color: '#114b32',
                  borderColor: '#b7e3c9',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">Produits du contrat</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ajoute des produits et rattache-les à un bloc.
                  </Typography>
                </Stack>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Autocomplete
                      options={articleCategoryOptions}
                      value={newProductCategory}
                      onChange={(_, value) => {
                        setNewProductCategory(value || '');
                        setNewProductSubCategory('');
                        setNewProductId('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Catégorie"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                            '& .MuiInputLabel-root': { color: '#2c6b4f' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Autocomplete
                      options={articleSubCategoryOptions}
                      value={newProductSubCategory}
                      onChange={(_, value) => {
                        setNewProductSubCategory(value || '');
                        setNewProductId('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sous-catégorie"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                            '& .MuiInputLabel-root': { color: '#2c6b4f' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Autocomplete
                      freeSolo
                      options={serviceSectionOptions}
                      value={newProductSection}
                      onInputChange={(_, value) => setNewProductSection(normalizeSectionLabel(value || DEFAULT_SECTION))}
                      onChange={(_, value) => setNewProductSection(normalizeSectionLabel(value || DEFAULT_SECTION))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bloc"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                            '& .MuiInputLabel-root': { color: '#2c6b4f' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Autocomplete
                      options={selectableProductOptions}
                      value={articleOptions.find((option) => option.value === newProductId) || null}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      onChange={(_, value) => setNewProductId(value?.value || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Produit"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                            '& .MuiInputLabel-root': { color: '#2c6b4f' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 1 }}>
                    <IconButton
                      onClick={addProductFromSelector}
                      disabled={!newProductId}
                      sx={{
                        width: '100%',
                        height: '56px',
                        backgroundColor: '#1f7a52',
                        color: '#f4fff8',
                        '&:hover': { backgroundColor: '#176540' },
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                <TableContainer
                  sx={{
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid #b7e3c9',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      '& th': {
                        backgroundColor: '#e4f4eb',
                        borderBottom: '1px solid #c7e8d5',
                      },
                      '& td': {
                        borderBottom: '1px solid #e0f0e7',
                      },
                      '& .MuiTableRow-root:last-of-type td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="center">Ordre</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }}>Bloc</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }}>Produit</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="center">Type</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="center">Périodicité</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="right">Qté</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="right">Prix unitaire HT</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="right">Total ligne</TableCell>
                        <TableCell sx={{ color: '#1a5c3d', fontWeight: 700 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.articles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Typography variant="body2" sx={{ color: '#3f7a5f' }}>
                              Aucun produit sur ce contrat.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {groupedArticleRows.flatMap((group) => [
                        (
                          <TableRow
                            key={`group-header-${group.section}`}
                            sx={{ backgroundColor: '#d9efe3' }}
                            onDragOver={(event) => {
                              event.preventDefault();
                              if (draggingArticleIndex == null) return;
                              setDragOverTarget({ type: 'group', section: group.section });
                            }}
                            onDrop={() => {
                              if (group.rows.length > 0) {
                                dropArticleOn(group.rows[0].index, 'before');
                              }
                            }}
                          >
                            <TableCell
                              colSpan={9}
                              sx={{
                                fontWeight: 700,
                                color: '#1a5c3d',
                                borderTop:
                                  dragOverTarget?.type === 'group' && dragOverTarget?.section === group.section
                                    ? '3px solid #1f7a52'
                                    : undefined,
                              }}
                            >
                              {group.section}
                            </TableCell>
                          </TableRow>
                        ),
                        ...group.rows.map(({ line, index }) => (
                          <TableRow
                            key={`article-line-${index}`}
                            sx={{
                              opacity: draggingArticleIndex === index ? 0.5 : 1,
                              ...(dragOverTarget?.type === 'row' && dragOverTarget?.index === index
                                ? {
                                  '& > td': {
                                    borderTop: dragOverTarget.placement === 'before' ? '3px solid #1f7a52' : undefined,
                                    borderBottom: dragOverTarget.placement === 'after' ? '3px solid #1f7a52' : undefined,
                                  },
                                }
                                : {}),
                            }}
                            onDrop={(event) => {
                              const rowRect = event.currentTarget.getBoundingClientRect();
                              const isAfterHalf = event.clientY > (rowRect.top + rowRect.height / 2);
                              dropArticleOn(index, isAfterHalf ? 'after' : 'before');
                            }}
                            onDragOver={(event) => {
                              event.preventDefault();
                              if (draggingArticleIndex == null) return;
                              const rowRect = event.currentTarget.getBoundingClientRect();
                              const isAfterHalf = event.clientY > (rowRect.top + rowRect.height / 2);
                              setDragOverTarget({ type: 'row', index, placement: isAfterHalf ? 'after' : 'before' });
                            }}
                          >
                            <TableCell align="center" sx={{ width: 56 }}>
                              <IconButton
                                size="small"
                                draggable
                                onDragStart={(event) => startArticleDrag(event, index)}
                                onDragOver={(event) => event.preventDefault()}
                                onDragEnd={endArticleDrag}
                                sx={{ color: '#1a5c3d', cursor: 'grab' }}
                                title="Glisser pour réordonner"
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={serviceSectionOptions}
                                value={line.section || DEFAULT_SECTION}
                                onInputChange={(_, value) => updateArticleLine(index, 'section', normalizeSectionLabel(value || DEFAULT_SECTION))}
                                onChange={(_, value) => updateArticleLine(index, 'section', normalizeSectionLabel(value || DEFAULT_SECTION))}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    sx={{
                                      '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#114b32' }}>
                                {line.productName || 'Produit non sélectionné'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ width: 120 }}>
                              <Chip
                                size="small"
                                label={isServiceArticleLine(line) ? 'Service' : 'Produit'}
                                color={isServiceArticleLine(line) ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ width: 130 }}>
                              <Chip
                                size="small"
                                label={
                                  line.billingCycle === 'mensuel'
                                    ? 'Mensuel'
                                    : line.billingCycle === 'annuel'
                                      ? 'Annuel'
                                      : 'One shot'
                                }
                                color={
                                  line.billingCycle === 'mensuel'
                                    ? 'info'
                                    : line.billingCycle === 'annuel'
                                      ? 'warning'
                                      : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 140 }}>
                              <TextField
                                fullWidth
                                type="number"
                                size="small"
                                value={line.quantity}
                                onChange={(e) => updateArticleLine(index, 'quantity', e.target.value)}
                                sx={{
                                  '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 170 }}>
                              <TextField
                                fullWidth
                                type="number"
                                size="small"
                                value={line.unitPrice}
                                onChange={(e) => updateArticleLine(index, 'unitPrice', e.target.value)}
                                sx={{
                                  '& .MuiInputBase-root': { backgroundColor: '#ffffff', color: '#0f3f2a' },
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#9ed6b8' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" fontWeight={700} sx={{ color: '#114b32' }}>
                                {formatEuro(toNumber(line.quantity) * toNumber(line.unitPrice))}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ width: 80 }}>
                              <IconButton color="error" onClick={() => removeArticleLine(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )),
                        (
                          <TableRow key={`group-subtotal-${group.section}`} sx={{ backgroundColor: '#eef9f2' }}>
                            <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, color: '#1a5c3d' }}>
                              {`Sous-total (${group.subtotalUnitLabel})`}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#114b32' }}>
                              {formatEuro(group.subtotalDisplay)}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        ),
                      ])}

                      <TableRow>
                        <TableCell colSpan={7} align="right">
                          <Typography variant="subtitle2" sx={{ color: '#1a5c3d' }}>
                            Total lignes HT
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#114b32' }}>
                            {formatEuro(articlesTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} align="right">
                          <Typography variant="subtitle2" sx={{ color: '#1a5c3d' }}>
                            Total lignes mensuel
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2" sx={{ color: '#114b32' }}>
                            {formatEuro(linesMonthlyValue)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} align="right">
                          <Typography variant="subtitle2" sx={{ color: '#1a5c3d' }}>
                            Total lignes annuel
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2" sx={{ color: '#114b32' }}>
                            {formatEuro(linesAnnualValue)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Grid>

        </Grid>
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: '#f2fbf6',
            color: '#114b32',
            borderColor: '#b7e3c9',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Récapitulatif global</Typography>
          <TableContainer
            sx={{
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid #b7e3c9',
              backgroundColor: '#ffffff',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#e4f4eb', color: '#1a5c3d', fontWeight: 700 }}>Poste</TableCell>
                  <TableCell sx={{ backgroundColor: '#e4f4eb', color: '#1a5c3d', fontWeight: 700 }} align="right">Mensuel</TableCell>
                  <TableCell sx={{ backgroundColor: '#e4f4eb', color: '#1a5c3d', fontWeight: 700 }} align="right">Annuel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Services</TableCell>
                  <TableCell align="right">{formatEuro(serviceMonthlyValue)}</TableCell>
                  <TableCell align="right">{formatEuro(serviceAnnualValue)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Produits</TableCell>
                  <TableCell align="right">{formatEuro(productsMonthlyValue)}</TableCell>
                  <TableCell align="right">{formatEuro(productsAnnualValue)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total global</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatEuro(globalMonthlyValue)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatEuro(globalAnnualValue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {error ? (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
